import React, { useContext, useEffect, useState } from 'react';
import * as S from './styled';
import { useSnackbar } from 'notistack';
import {
  defaultBlurDepositForm,
  defaultDepositForm,
} from '../../../../store/invoice/utils';
import Form from '../..';
import { ExportContext } from '../../../../provider/ExportProvider';
import { maskFieldDate, unmaskFieldDate } from '../utils';
import { formatBRL, formatCents } from '../../../../store/masks';
import { useAbility } from '../../../Hooks/useAbility';

export default function FormDepositUpdate() {
  const {
    deposits,
    getInvoice,
    setInvoice,
    updateDeposit,
    setModal,
    getFormMessage,
    validateForm,
    filterSelectCompanies,
    setCompanyFilterField,
    selectCompanies,
    filterDailyDeposit,
    filterDeposit,
    depositGroupItems,
    depositGroups,
    selectedGroupId,
  } = useContext(ExportContext)!.controllers.deposit;
  const { ability } = useAbility();

  const { enqueueSnackbar } = useSnackbar();

  const getForm = <T extends FlattenKeys<DepositForm>>(field: T) =>
    getInvoice(`forms.deposits.update.${field}`);

  const setForm = <
    T extends FlattenKeys<DepositForm>,
    V extends DeepType<InvoiceState, `forms.deposits.update.${T}`>,
  >(
    field: T,
    value: V,
  ) => setInvoice(`forms.deposits.update.${field}`, value);

  const blurField = (field: keyof DepositForm) => () => {
    setInvoice(`blur.deposits.update.${field}`, true);
  };

  const clearForm = () => {
    const deposit = deposits.find(
      ({ id }) => getInvoice('forms.deposits.update').id === id,
    )!;
    setInvoice(`forms.deposits.update`, {
      id: deposit.id,
      value: deposit.value,
      date: deposit.date,
      reference: deposit.reference,
      locked: deposit.locked,
      company: {
        id: deposit.company.id,
        name: deposit.company.name,
      },
      group: {
        id: deposit.group?.id ?? 0,
        number: deposit.group?.number ?? 0,
        name: deposit.group?.name ?? '',
      },
    });
    setInvoice(`blur.deposits.update`, defaultBlurDepositForm());
  };

  const messages = getFormMessage('deposits.update');

  const [loading, setLoading] = React.useState(false);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm('deposits.update')
      .then(() => {
        setLoading(true);
        updateDeposit()
          .then(() => {
            filterDeposit()
            enqueueSnackbar('Depósito atualizado com sucesso', {
              variant: 'success',
            });
            setInvoice('forms.deposits.update', {
              id: 0,
              ...defaultDepositForm(),
            });
            setModal('single.updateDeposit', false);
            filterDailyDeposit();
          })
          .catch(() => {
            enqueueSnackbar('Houve uma falha ao atualizar o depósito', {
              variant: 'error',
            });
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        enqueueSnackbar('Verifique a validade dos campos', {
          variant: 'error',
        });
      });
  };

  const [companyLoading, setCompanyLoading] = useState(false);
  const filterCompanies = () => {
    setCompanyLoading(true);
    filterSelectCompanies().finally(() => {
      setCompanyLoading(false);
    });
  };

  const locked = getForm('locked');

  const group = getForm('group')

  useEffect(() => {
    if (!selectedGroupId && (!group.id && depositGroups.length > 0))
      setForm('group', depositGroups[0]);
  }, [depositGroups, selectedGroupId]);

  return (
    <S.Form
      style={{
        maxHeight: '80vh',
      }}
      onSubmit={onSubmit}
    >
      <S.Body>
        <S.Grid
          style={{
            gridTemplateColumns: !selectedGroupId && (depositGroupItems.length > 1) ? '1fr 1fr 1fr 1fr 1fr auto' : '1fr 1fr 1fr 1fr auto'
          }}
        >
          <Form.Input
            label="Valor"
            value={getForm('value')}
            onChange={(e) => setForm('value', +(e.target.value || 0))}
            error={messages.value}
            onBlur={blurField('value')}
            mask={formatBRL}
            unmask={formatCents}
            disabled={locked}
          />

          <Form.Input.Dropdown
            label="Companhia"
            items={selectCompanies.map(({ id, name }) => ({ id, name }))}
            value={getForm('company.name')}
            onChange={(e) => {
              const value = e.target.value;
              setForm('company.name', value);
              setCompanyFilterField('name')(e);
            }}
            onDebouncedChange={filterCompanies}
            error={messages.company}
            onBlur={blurField('company')}
            onFocus={() => {
              setCompanyFilterField('name')({ target: { value: '' } });
            }}
            onSelected={({ id }) => {
              setForm('company.id', +(id || 0));
            }}
            loading={companyLoading}
            disabled={locked}
          />

          <Form.Input.DatePicker
            label="Data do depósito"
            value={maskFieldDate(getForm('date'))}
            onChange={(e) => setForm('date', unmaskFieldDate(e.target.value))}
            error={messages.date}
            onBlur={blurField('date')}
            disabled={locked}
          />

          <Form.Input.DatePicker
            label="Data de referência"
            value={maskFieldDate(getForm('reference'))}
            onChange={(e) =>
              setForm('reference', unmaskFieldDate(e.target.value))
            }
            error={messages.reference}
            onBlur={blurField('reference')}
            disabled={locked}
          />

          {ability?.can('update', 'Invoice', 'groupId') && !selectedGroupId && (depositGroupItems.length > 1) ? (
            <Form.Input.Dropdown
              label="Grupo"
              items={depositGroupItems}
              value={
                depositGroupItems.find(({ id }) => id === getForm('group')?.id)?.name ??
                ''
              }
              error={messages.group}
              onBlur={blurField('group')}
              onSelected={(item) => {
                setForm('group', item.data);
              }}
              disabled={depositGroupItems.length === 1}
            />
          ) : null}
        </S.Grid>
      </S.Body>
      <S.Actions>
        <Form.Button type="button" onClick={clearForm} disabled={locked}>
          Limpar
        </Form.Button>
        <Form.Button loading={loading} disabled={locked}>
          Atualizar
        </Form.Button>
      </S.Actions>
    </S.Form>
  );
}
